import React, { useState } from 'react';
import { authApi } from '../api/auth.js';

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Read and clear login info message from sessionStorage (if any)
  const [infoMessage, setInfoMessage] = useState(() => {
    const msg = sessionStorage.getItem('login_message');
    if (msg) {
      sessionStorage.removeItem('login_message');
    }
    return msg || '';
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Email and password are required.'); return; }

    setError('');
    setLoading(true);
    try {
      const { user } = await authApi.login({ email, password });

      const allowedRoles = ['admin', 'software_developer', 'sales_tl'];
      if (!allowedRoles.includes(user.role)) {
        throw new Error(`Access denied`);
      }

      const role = (user.role === 'sales_tl') ? 'teamleader' : user.role;
      onLogin({ ...user, role, loginTime: new Date().toISOString() });
    } catch (err) {
      setError(err.message || 'Login failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const formCard = (
    <div className="login-card">
      <div className="login-brand"><span>M</span>eetel</div>
      <div className="login-sub">Sales Management System</div>
      <div className="login-welcome">Welcome Back!</div>
      <div className="login-welcome-sub">Sign in to continue</div>

      <form onSubmit={handleSubmit}>

        <div className="form-group">
          <label className="form-label">Email</label>
          <input
            className="form-control"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="email@company.com"
            autoComplete="email"
            disabled={loading}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Password</label>
          <input
            className="form-control"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="••••••••••••"
            autoComplete="current-password"
            disabled={loading}
          />
        </div>

        {infoMessage && !error && (
          <div style={{
            marginBottom: 16, padding: '10px 14px', borderRadius: 8,
            background: '#fef3c7', border: '1px solid #fde68a',
            color: '#d97706', fontSize: 13, fontWeight: 500,
          }}>
            {infoMessage}
          </div>
        )}

        {error && (
          <div style={{
            marginBottom: 16, padding: '10px 14px', borderRadius: 8,
            background: '#fef2f2', border: '1px solid #fecaca',
            color: '#dc2626', fontSize: 13, fontWeight: 500,
          }}>
            {error}
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary w-full"
          style={{ justifyContent: 'center', padding: '12px', fontSize: 14 }}
          disabled={loading}
        >
          {loading ? 'Signing in…' : 'Sign In'}
        </button>
      </form>
    </div>
  );

  return (
    <>
      {/* ── DESKTOP layout: two-column ── */}
      <div className="login-page login-desktop">
        <div className="login-left">
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg,#0f172a 0%,#1e3a5f 50%,#0f4c75 100%)' }} />
          <div style={{ position: 'absolute', inset: 0, backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/svg%3E")` }} />
          <div className="login-left-text" style={{ position: 'relative', zIndex: 1 }}>
            <svg viewBox="0 0 400 160" width="100%" style={{ opacity: .3, marginBottom: 20 }}>
              <rect x="0" y="80" width="400" height="80" fill="#0f4c75" />
              <rect x="20" y="50" width="30" height="110" fill="#1a5276" /><rect x="60" y="30" width="20" height="130" fill="#1a5276" />
              <rect x="90" y="60" width="40" height="100" fill="#1a5276" /><rect x="140" y="20" width="25" height="140" fill="#1a5276" />
              <rect x="175" y="40" width="35" height="120" fill="#1a5276" /><rect x="220" y="55" width="30" height="105" fill="#1a5276" />
              <rect x="260" y="25" width="20" height="135" fill="#1a5276" /><rect x="290" y="45" width="40" height="115" fill="#1a5276" />
              <rect x="340" y="35" width="25" height="125" fill="#1a5276" />
              <line x1="60" y1="0" x2="0" y2="80" stroke="#4a9fd4" strokeWidth="1.5" opacity=".5" />
              <line x1="60" y1="0" x2="120" y2="80" stroke="#4a9fd4" strokeWidth="1.5" opacity=".5" />
              <line x1="260" y1="0" x2="200" y2="80" stroke="#4a9fd4" strokeWidth="1.5" opacity=".5" />
              <line x1="260" y1="0" x2="320" y2="80" stroke="#4a9fd4" strokeWidth="1.5" opacity=".5" />
              <rect x="0" y="78" width="400" height="4" fill="#4a9fd4" opacity=".4" />
            </svg>
            <h1>Manage Your<br />Sales Team<br />Effortlessly</h1>
            <p>Track visits, orders and performance<br />all in one place.</p>
          </div>
          <div style={{ position: 'absolute', bottom: 16, left: 40, color: 'rgba(255,255,255,.3)', fontSize: 11, zIndex: 1 }}>
            © 2024 Meetel. All rights reserved.
          </div>
        </div>
        <div className="login-right">{formCard}</div>
      </div>

      {/* ── MOBILE layout: full-screen card ── */}
      <div className="login-mobile">
        <div style={{
          minHeight: '100vh',
          background: 'linear-gradient(160deg,#0f172a 0%,#1e3a5f 60%,#0f4c75 100%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '24px 20px',
        }}>
          <div style={{ position: 'fixed', top: -60, right: -60, width: 200, height: 200, background: 'rgba(37,99,235,.15)', borderRadius: '50%', pointerEvents: 'none' }} />
          <div style={{ position: 'fixed', bottom: -80, left: -60, width: 240, height: 240, background: 'rgba(37,99,235,.1)', borderRadius: '50%', pointerEvents: 'none' }} />
          <div style={{
            background: '#fff', borderRadius: 24, padding: '28px 24px',
            width: '100%', maxWidth: 400,
            boxShadow: '0 24px 64px rgba(0,0,0,.35)',
            position: 'relative', zIndex: 1,
          }}>
            {formCard}
          </div>
          <div style={{ color: 'rgba(255,255,255,.3)', fontSize: 11, marginTop: 20, position: 'relative', zIndex: 1 }}>
            © 2024 Meetel. All rights reserved.
          </div>
        </div>
      </div>
    </>
  );
}
